import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
} from "recharts";
import { api, health, listOrdersByMerchant } from "../services/api";
import { useMerchantStore } from "../store/useMerchantStore";
import {
  buildDailyOrderBuckets,
  countByOrderStatus,
  ordersTotals,
  orderStatusPieData,
  parseMoney,
  parseDistanceKm,
  routesStatusSeries,
  summarizeRoutes,
} from "../utils/merchantDashboardAggregate";
import {
  summarizeMerchantRoutePipeline,
  classifyMerchantRoute,
} from "../utils/merchantRouteTracking";
import { PhoneLink, StatusPill, statusLabel } from "../components/orderKit";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

const BG_CARD = "#ffffff";
const BORDER = "#e6e1f0";
const MUTED = "#6a6480";
const ACCENT = "#6b4eaa";

const ROUTE_BAR_COLORS = {
  DRAFT: "#8c8595",
  CONFIRMED: "#6b4eaa",
  BROADCASTED: "#b8955a",
  ASSIGNED: "#1f4e79",
  COMPLETED: "#2f7a57",
};

function fmtEtb(n) {
  if (!Number.isFinite(n)) return "-";
  return t("common.amount_etb", { amount: n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) });
}

function fmtInt(n) {
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString();
}

function DashTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: BG_CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        padding: "10px 14px",
        color: "#1b1530",
        boxShadow: "0 8px 24px rgba(46, 33, 84, 0.14)",
      }}
    >
      {label != null && (
        <div style={{ fontWeight: 700, marginBottom: 6, color: "#6b4eaa" }}>{label}</div>
      )}
      {payload.map((p) => (
        <div key={String(p.dataKey)} style={{ fontSize: 13, marginTop: 2 }}>
          {p.name}:{" "}
          {typeof p.value === "number"
            ? p.dataKey === "revenue"
              ? fmtEtb(p.value)
              : p.value.toLocaleString()
            : p.value}
        </div>
      ))}
    </div>
  );
}

function KpiCard({ title, value, hint }) {
  return (
    <div
      style={{
        padding: "18px 20px",
        borderRadius: 16,
        border: `1px solid ${BORDER}`,
        background: BG_CARD,
        boxShadow: "var(--shadow-soft)",
        minWidth: 160,
        flex: "1 1 160px",
      }}
    >
      <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{title}</div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 26, marginTop: 8, color: "#1b1530", fontWeight: 600 }}>
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: 11, color: MUTED, marginTop: 8, lineHeight: 1.35 }}>{hint}</div>
      ) : null}
    </div>
  );
}

export function Dashboard() {
  const { merchantId, merchantName } = useMerchantStore();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    health().then(setStatus).catch(() => setStatus({ status: "down" }));
  }, []);

  const loadDashboard = useCallback(async () => {
    if (merchantId == null || !Number.isFinite(Number(merchantId))) return;
    setLoading(true);
    setError(null);
    try {
      const [ordersData, routesRes] = await Promise.all([
        listOrdersByMerchant(merchantId),
        api.get("/api/orders/routes", {
          params: {
            merchantId,
            status: "DRAFT,CONFIRMED,BROADCASTED,ASSIGNED,COMPLETED",
          },
        }),
      ]);
      setOrders(Array.isArray(ordersData) ? ordersData : []);
      const rd = routesRes?.data;
      setRoutes(Array.isArray(rd) ? rd : []);
    } catch (e) {
      const msg = friendlyError(e, t("dash.load_failed"));
      setError(msg);
      setOrders([]);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const orderMetrics = useMemo(() => ordersTotals(orders), [orders]);
  const statusCounts = useMemo(() => countByOrderStatus(orders), [orders]);
  const pieData = useMemo(
    () =>
      orderStatusPieData(statusCounts)
        .filter((d) => d.value > 0)
        .map((d) => ({ ...d, name: statusLabel(d.name) })),
    [statusCounts]
  );
  const daily = useMemo(() => buildDailyOrderBuckets(orders, 30), [orders]);
  const routeSummary = useMemo(() => summarizeRoutes(routes), [routes]);
  const routeBars = useMemo(() => routesStatusSeries(routeSummary.byStatus), [routeSummary.byStatus]);
  const logistics = useMemo(
    () => summarizeMerchantRoutePipeline(routes, merchantId),
    [routes, merchantId]
  );

  const attention = useMemo(() => {
    const routeCount = (st) => routes.filter((r) => String(r.status || "").toUpperCase() === st).length;
    const items = [
      { count: statusCounts.CREATED || 0, one: "dash.att.pack_one", many: "dash.att.pack_many", icon: "fa-clipboard-check", to: "/fulfill", tone: "gold" },
      { count: statusCounts.READY || 0, one: "dash.att.ready_one", many: "dash.att.ready_many", icon: "fa-box", to: "/ready-orders", tone: "blue" },
      { count: routeCount("DRAFT"), one: "dash.att.draft_one", many: "dash.att.draft_many", icon: "fa-route", to: "/planned-routes", tone: "plum" },
      { count: routeCount("CONFIRMED"), one: "dash.att.confirmed_one", many: "dash.att.confirmed_many", icon: "fa-tower-broadcast", to: "/confirmed-routes", tone: "plum" },
      { count: logistics.awaiting || 0, one: "dash.att.awaiting_one", many: "dash.att.awaiting_many", icon: "fa-hourglass-half", to: "/broadcasted-routes", tone: "muted" },
    ];
    return items
      .filter((i) => i.count > 0)
      .map((i) => ({ ...i, label: t(i.count === 1 ? i.one : i.many) }));
  }, [statusCounts, routes, logistics]);

  const recentOrders = useMemo(() => {
    return [...orders]
      .sort((a, b) => {
        const tb = Date.parse(b.createdAt) || 0;
        const ta = Date.parse(a.createdAt) || 0;
        return tb - ta;
      })
      .slice(0, 20);
  }, [orders]);

  const recentRoutes = useMemo(() => {
    return [...routes]
      .sort((a, b) => Number(b.routeId) - Number(a.routeId))
      .slice(0, 15);
  }, [routes]);

  const exportOrdersSheet = () => {
    const rows = orders.map((o) => ({
      [t("dash.col.id")]: o.id,
      [t("dash.col.created")]: o.createdAt,
      [t("dash.col.status")]: statusLabel(o.status),
      [t("dash.col.total_etb")]: parseMoney(o.total),
      [t("dash.col.phone")]: o.clientPhone || "",
      [t("dash.col.address")]: (o.deliveryAddress || "").slice(0, 200),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("nav.orders"));
    XLSX.writeFile(wb, `odos-merchant-orders-${Date.now()}.xlsx`);
  };

  const exportRoutesSheet = () => {
    const rows = routes.map((r) => ({
      [t("dash.col.route_id")]: r.routeId,
      [t("dash.col.status")]: statusLabel(r.status),
      [t("route.stats.stops")]: Array.isArray(r.orderedOrderIds) ? r.orderedOrderIds.length : 0,
      [t("dash.col.distance_km")]: parseDistanceKm(r.totalDistanceKm),
      [t("dash.col.route_earning_etb")]: parseMoney(r.driverTotalEarning),
      [t("dash.col.odos_fee_etb")]: parseMoney(r.serviceFeeEtb),
      [t("dash.col.direct_to_driver_etb")]: parseMoney(r.netDriverPayoutEtb),
      [t("routes.payment")]: r.paymentStatus || "",
      [t("dash.col.driver")]: r.driverId || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("route.stats.routes"));
    XLSX.writeFile(wb, `odos-merchant-routes-${Date.now()}.xlsx`);
  };

  const signedIn = merchantId != null && Number.isFinite(Number(merchantId));

  return (
    <div className="page-container dashboard-full-width">
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <div className="hero-banner" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 16, alignItems: "flex-end" }}>
          <div>
            <div className="eyebrow">{t("dash.eyebrow")}</div>
            <h1 style={{ margin: "6px 0 6px", color: "#ffffff", fontSize: 34 }}>{t("nav.dashboard")}</h1>
            {merchantName ? (
              <p style={{ margin: 0, color: "rgba(255,255,255,0.72)" }}>
                {t("dash.signed_in_as")} <strong style={{ color: "#ffffff" }}>{merchantName}</strong>
                {merchantId != null ? <span style={{ opacity: 0.65 }}> · #{merchantId}</span> : null}
              </p>
            ) : (
              <p style={{ margin: 0, color: "rgba(255,255,255,0.72)" }}>{t("dash.welcome")}</p>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              onClick={loadDashboard}
              disabled={loading || !signedIn}
              style={{
                padding: "10px 18px",
                borderRadius: 12,
                border: "none",
                background: loading ? "#a59eae" : "var(--gold)",
                color: "#ffffff",
                fontWeight: 600,
                cursor: loading ? "wait" : "pointer",
              }}
            >
              {loading ? t("dash.refreshing") : t("dash.refresh_data")}
            </button>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.08)",
                fontSize: 13,
              }}
            >
              <span style={{ color: "rgba(255,255,255,0.7)" }}>API </span>
              {status?.status === "ok" || status?.status === "UP" ? (
                <span style={{ color: "#9fd8b9", fontWeight: 700 }}>● {t("dash.connected")}</span>
              ) : (
                <span style={{ color: "#f2b8b0", fontWeight: 700 }}>● {status?.status ?? t("dash.unknown")}</span>
              )}
            </div>
          </div>
        </div>

        {!signedIn && (
          <p style={{ color: "#a63a3a", marginTop: 16 }}>
            {t("dash.sign_in")}
          </p>
        )}

        {error && signedIn && (
          <div style={{ marginTop: 16, padding: 12, borderRadius: 10, background: "#fbefec", color: "#a63a3a", border: "1px solid #e3bdb6" }}>
            {error}
          </div>
        )}

        {signedIn && !loading && !error && orders.length === 0 && routes.length === 0 && (
          <p style={{ color: MUTED, marginTop: 16 }}>
            {t("dash.no_data")}
          </p>
        )}

        {signedIn && (
          <>
            {/* Needs your attention (app: AttentionTile row) */}
            <h3 className="section-title">{t("dash.attention")}</h3>
            {attention.length === 0 ? (
              <div className="attention-clear">
                <i className="fa-solid fa-circle-check" aria-hidden="true" />
                <div>
                  <strong>{t("dash.caught_up")}</strong> {t("dash.caught_up_detail")}
                </div>
              </div>
            ) : (
              <div className="attention-grid">
                {attention.map((a) => (
                  <Link key={a.to} to={a.to} className={`attention-tile attention-tile--${a.tone}`}>
                    <span className="attention-tile__icon">
                      <i className={`fa-solid ${a.icon}`} aria-hidden="true" />
                    </span>
                    <span className="attention-tile__copy">
                      <span className="attention-tile__count">{a.count}</span>
                      <span className="attention-tile__label">{a.label}</span>
                    </span>
                    <i className="fa-solid fa-chevron-right attention-tile__chevron" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            )}

            {/* KPI - Orders / sales */}
            <h3 className="section-title">{t("dash.sales_orders")}</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <KpiCard title={t("dash.kpi.orders")} value={fmtInt(orderMetrics.count)} hint={t("dash.kpi.orders_hint")} />
              <KpiCard title={t("dash.kpi.gmv")} value={fmtEtb(orderMetrics.gmv)} hint={t("dash.kpi.gmv_hint")} />
              <KpiCard title={t("dash.kpi.avg")} value={fmtEtb(orderMetrics.avgOrderValue)} />
              <KpiCard
                title={t("status.delivered")}
                value={fmtInt(orderMetrics.delivered)}
                hint={t("dash.kpi.delivered_hint", { pct: (orderMetrics.deliveredRate * 100).toFixed(1) })}
              />
              <KpiCard title={t("status.cancelled")} value={fmtInt(orderMetrics.cancelled)} />
            </div>

            {/* KPI - Routes / delivery pool */}
            <h3 className="section-title">
              {t("dash.routes_economics")}
            </h3>
            <p style={{ color: MUTED, fontSize: 13, marginTop: -6, marginBottom: 12, maxWidth: 720, lineHeight: 1.45 }}>
              {t("dash.economics_note")}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <KpiCard
                title={t("dash.kpi.awaiting")}
                value={fmtInt(logistics.awaiting)}
                hint={t("dash.kpi.awaiting_hint")}
              />
              <KpiCard
                title={t("dash.kpi.accepted")}
                value={fmtInt(logistics.acceptedPendingDispatch)}
                hint={t("dash.kpi.accepted_hint")}
              />
              <KpiCard
                title={t("dash.kpi.dispatched")}
                value={fmtInt(logistics.dispatchedActive)}
                hint={t("dash.kpi.dispatched_hint")}
              />
              <KpiCard
                title={t("route.stats.routes")}
                value={fmtInt(routeSummary.count)}
                hint={t("dash.kpi.completed_hint", { n: fmtInt(routeSummary.completedRoutes) })}
              />
              <KpiCard title={t("dash.kpi.pipeline")} value={fmtInt(routeSummary.activePipeline)} />
              <KpiCard title={t("dash.kpi.stops")} value={fmtInt(routeSummary.stopsOnRoutes)} />
              <KpiCard
                title={t("dash.kpi.total_km")}
                value={t("common.km", { n: routeSummary.totalKm.toLocaleString(undefined, { maximumFractionDigits: 1 }) })}
              />
              <KpiCard title={t("dash.kpi.pool")} value={fmtEtb(routeSummary.sumDriverTotal)} hint={t("dash.kpi.pool_hint")} />
              <KpiCard title={t("dash.kpi.fees")} value={fmtEtb(routeSummary.sumServiceFee)} />
              <KpiCard title={t("dash.kpi.direct")} value={fmtEtb(routeSummary.sumNetDriver)} />
            </div>

            {/* Charts row 1 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
                gap: 16,
                marginTop: 24,
              }}
            >
              <div className="panel" style={{ padding: 18 }}>
                <div className="panel-title">{t("dash.chart.last30")}</div>
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>{t("dash.chart.last30_hint")}</div>
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#ede9f6" strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: MUTED, fontSize: 10 }} interval={4} />
                      <YAxis yAxisId="left" tick={{ fill: MUTED, fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: MUTED, fontSize: 11 }} allowDecimals={false} />
                      <Tooltip content={<DashTooltip />} />
                      <Legend wrapperStyle={{ color: MUTED }} />
                      <Bar
                        yAxisId="left"
                        dataKey="revenue"
                        name={t("dash.chart.revenue")}
                        fill="#6b4eaa"
                        radius={[4, 4, 0, 0]}
                        opacity={0.9}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="orderCount"
                        name={t("nav.orders")}
                        stroke="#b8955a"
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="panel" style={{ padding: 18 }}>
                <div className="panel-title">{t("dash.chart.by_status")}</div>
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>{t("dash.chart.by_status_hint")}</div>
                <div style={{ width: "100%", height: 300 }}>
                  {pieData.length === 0 ? (
                    <div style={{ color: MUTED, padding: 40, textAlign: "center" }}>{t("dash.chart.no_breakdown")}</div>
                  ) : (
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                        >
                          {pieData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} stroke="#f8f6fc" strokeWidth={1} />
                          ))}
                        </Pie>
                        <Tooltip content={<DashTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            {/* Charts row 2 */}
            <div style={{ marginTop: 16 }}>
              <div className="panel" style={{ padding: 18 }}>
                <div className="panel-title">{t("dash.chart.routes_lifecycle")}</div>
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>{t("dash.chart.routes_lifecycle_hint")}</div>
                <div style={{ width: "100%", height: 280 }}>
                  {routeBars.length === 0 ? (
                    <div style={{ color: MUTED, padding: 40, textAlign: "center" }}>{t("dash.no_routes_yet")}</div>
                  ) : (
                    <ResponsiveContainer>
                      <BarChart data={routeBars} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid stroke="#ede9f6" strokeDasharray="3 3" />
                        <XAxis dataKey="status" tick={{ fill: MUTED, fontSize: 11 }} tickFormatter={(s) => statusLabel(s)} />
                        <YAxis tick={{ fill: MUTED, fontSize: 11 }} allowDecimals={false} />
                        <Tooltip content={<DashTooltip />} />
                        <Bar dataKey="count" name={t("route.stats.routes")} radius={[6, 6, 0, 0]}>
                          {routeBars.map((entry) => (
                            <Cell key={entry.status} fill={ROUTE_BAR_COLORS[entry.status] || "#6a6480"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            {/* Tables */}
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12, marginTop: 20, alignItems: "center" }}>
              <h3 className="section-title" style={{ margin: 0 }}>{t("dash.reports")}</h3>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={exportOrdersSheet}
                  disabled={orders.length === 0}
                  style={{
                    padding: "9px 16px",
                    borderRadius: 12,
                    border: `1px solid ${BORDER}`,
                    background: orders.length === 0 ? "#f1eef8" : "#ffffff",
                    color: orders.length === 0 ? "#a59eae" : "#6b4eaa",
                    fontWeight: 600,
                    cursor: orders.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  {t("dash.download_orders")}
                </button>
                <button
                  type="button"
                  onClick={exportRoutesSheet}
                  disabled={routes.length === 0}
                  style={{
                    padding: "9px 16px",
                    borderRadius: 12,
                    border: `1px solid ${BORDER}`,
                    background: routes.length === 0 ? "#f1eef8" : "#ffffff",
                    color: routes.length === 0 ? "#a59eae" : "#6b4eaa",
                    fontWeight: 600,
                    cursor: routes.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  {t("dash.download_routes")}
                </button>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
                gap: 16,
                marginTop: 12,
              }}
            >
              <div className="panel" style={{ overflow: "hidden" }}>
                <div className="panel-title" style={{ padding: "14px 16px", borderBottom: `1px solid ${BORDER}` }}>
                  {t("dash.recent_orders")}
                </div>
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: MUTED, background: "#fcfbfe", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("dash.col.id")}</th>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("dash.col.created")}</th>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("dash.col.status")}</th>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("dash.col.phone")}</th>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("dash.col.total")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((o) => (
                        <tr key={o.id} style={{ color: "#1b1530" }}>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>#{o.id}</td>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, color: MUTED, whiteSpace: "nowrap" }}>
                            {o.createdAt ? String(o.createdAt).replace("T", " ").slice(0, 19) : "-"}
                          </td>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>
                            <StatusPill status={o.status} />
                          </td>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>
                            {o.clientPhone ? <PhoneLink phone={o.clientPhone} /> : "-"}
                          </td>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, fontWeight: 600 }}>
                            {fmtEtb(parseMoney(o.total))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {recentOrders.length === 0 && (
                    <div style={{ padding: 24, color: MUTED, textAlign: "center" }}>{t("dash.no_orders")}</div>
                  )}
                </div>
              </div>

              <div className="panel" style={{ overflow: "hidden" }}>
                <div className="panel-title" style={{ padding: "14px 16px", borderBottom: `1px solid ${BORDER}` }}>
                  {t("dash.recent_routes")}
                </div>
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: MUTED, background: "#fcfbfe", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("dash.col.route")}</th>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("dash.col.status")}</th>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("route.stats.stops")}</th>
                        <th style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>{t("dash.col.fee_earning")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentRoutes.map((r) => {
                        const logBucket = classifyMerchantRoute(r, merchantId);
                        const logLabel =
                          logBucket === "awaiting"
                            ? t("dash.kpi.awaiting")
                            : logBucket === "accepted"
                              ? t("dash.accepted")
                              : logBucket === "dispatched"
                                ? t("status.dispatched")
                                : logBucket === "completed"
                                  ? t("status.completed")
                                  : null;
                        return (
                        <tr key={r.routeId} style={{ color: "#1b1530" }}>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, fontWeight: 600 }}>
                            #{r.routeId}
                          </td>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>
                            <StatusPill status={r.status} />
                            {logLabel ? (
                              <div style={{ marginTop: 4, fontSize: 11, color: "#8467c2", fontWeight: 600 }}>
                                {logLabel}
                              </div>
                            ) : null}
                          </td>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>
                            {Array.isArray(r.orderedOrderIds) ? r.orderedOrderIds.length : 0}
                          </td>
                          <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, fontSize: 12, lineHeight: 1.35 }}>
                            <div style={{ color: "#8467c2" }}>{t("dash.fee", { amount: fmtEtb(parseMoney(r.serviceFeeEtb)) })}</div>
                            <div style={{ color: MUTED }}>{t("dash.pool", { amount: fmtEtb(parseMoney(r.driverTotalEarning)) })}</div>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                  {recentRoutes.length === 0 && (
                    <div style={{ padding: 24, color: MUTED, textAlign: "center" }}>{t("dash.no_routes")}</div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 28, display: "flex", flexWrap: "wrap", gap: 12 }}>
              <Link
                to="/catalog"
                style={{
                  display: "inline-block",
                  padding: "10px 18px",
                  borderRadius: 12,
                  backgroundColor: ACCENT,
                  color: "#ffffff",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                {t("dash.product_catalog")}
              </Link>
              <Link
                to="/orders"
                style={{
                  display: "inline-block",
                  padding: "10px 18px",
                  borderRadius: 12,
                  border: "1px solid #cfc7e0",
                  background: "#ffffff",
                  color: ACCENT,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                {t("nav.orders")}
              </Link>
              <Link
                to="/broadcasted-routes"
                style={{
                  display: "inline-block",
                  padding: "10px 18px",
                  borderRadius: 12,
                  border: "1px solid #cfc7e0",
                  background: "#ffffff",
                  color: ACCENT,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                {t("nav.broadcasted_routes")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
